import { useEffect, useState } from 'react'
import { LEGAL } from '@/config/legal'

// SPA fallback for the DSAR verification confirmation page.
//
// dsar-verify (edge fn) 302-redirects to `/dsar-complete.html?status=...&type=...`
// on this origin. Normally the static public/dsar-complete.html (+ dsar-complete.js)
// handles it — allowed through by vercel.json's rewrite lookahead and the PWA
// service worker's navigateFallbackDenylist. But if a service worker serves the
// SPA app-shell for that navigation instead (most importantly a STALE pre-denylist
// worker that hasn't updated yet), React Router would otherwise have no matching
// route and the page would render BLANK. This route renders the SAME per-status
// confirmation card so the data subject always sees a styled confirmation, whether
// the static page OR the app-shell is served.
//
// Unlike OAuthComplete this is a full-tab email-link page: there is no opener, so
// no postMessage and no auto-close — it only renders a card and a link home.

const TYPE_LABELS: Record<string, string> = {
  access: 'data access (export)',
  delete: 'data deletion',
  correct: 'data correction',
  opt_out: 'opt-out',
}

interface Card {
  badge: string
  ok: boolean
  heading: string
  body: string
  typeLabel?: string
  // When set, the card shows a concrete recovery action: a "resubmit" link back
  // to /dsar and (if a support email is configured) a mailto contact. Used by
  // the error fallback so the data subject is never left with a dead end.
  recover?: boolean
}

function cardFor(status: string | null, type: string | null): Card {
  switch (status) {
    case 'verified':
      return {
        badge: 'Verified',
        ok: true,
        heading: 'Your request has been confirmed',
        body: 'request has been verified. Our team will review and process it within 45 calendar days as required by the California Consumer Privacy Act (CCPA/CPRA).',
        typeLabel: (type && (TYPE_LABELS[type] ?? type)) || 'data',
      }
    case 'already':
      return {
        badge: 'Already Confirmed',
        ok: true,
        heading: 'Your request is confirmed',
        body: 'Your data request has already been verified. Our team will process it and contact you with a response within 45 days as required by law.',
      }
    case 'expired':
      return {
        badge: 'Link Expired',
        ok: false,
        heading: 'This verification link has expired',
        body: 'Verification links are valid for 24 hours. Please resubmit your request to receive a new link.',
      }
    case 'invalid':
      return {
        badge: 'Invalid Link',
        ok: false,
        heading: 'This link is invalid',
        body: 'The verification link you followed is missing or malformed. Please check your email and try again.',
      }
    case 'notfound':
      return {
        badge: 'Not Found',
        ok: false,
        heading: 'This link was not found',
        body: 'The verification link may have already been used or never existed. If you believe this is an error, please resubmit your request.',
      }
    default:
      return {
        badge: 'Error',
        ok: false,
        heading: 'We could not verify your request',
        body: 'The verification link may have expired or been used already. Resubmit your request from the privacy request page to receive a fresh link.',
        recover: true,
      }
  }
}

export function DsarComplete() {
  const [card, setCard] = useState<Card | null>(null)

  useEffect(() => {
    const params = new URLSearchParams(window.location.search)
    // I approve bandaid: static result page that derives its card once from
    // window.location on mount — a legitimate sync setState in a mount-only
    // effect, not a render-loop hazard. (Same disposition as Analytics/Commissions.)
    // eslint-disable-next-line react-hooks/set-state-in-effect
    setCard(cardFor(params.get('status'), params.get('type')))
  }, [])

  if (!card) return null

  return (
    <div
      style={{
        // Dark brand to match the app shell (src/index.css @theme) and the
        // static public/dsar-complete.html sibling: bg #09090B, card #0F0F12,
        // text #FAFAFA, border #27272A, muted #A1A1AA, accent #E11D48.
        fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", sans-serif',
        color: '#FAFAFA',
        background: '#09090B',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        minHeight: '100vh',
        margin: 0,
        padding: 24,
      }}
    >
      <div
        style={{
          background: '#0F0F12',
          border: '1px solid #27272A',
          borderRadius: 12,
          padding: '40px 48px',
          maxWidth: 480,
          width: '100%',
          textAlign: 'center',
        }}
      >
        <div style={{ fontSize: 20, fontWeight: 800, letterSpacing: '-0.04em', marginBottom: 28, color: '#FAFAFA' }}>
          rize<span style={{ color: '#E11D48' }}>.</span>
        </div>
        <span
          style={{
            // Green/red kept legible on the dark card via light-400 text on a
            // low-alpha fill (mirrors the app's dark-mode status pills).
            display: 'inline-block',
            background: card.ok ? 'rgba(34,197,94,0.12)' : 'rgba(220,38,38,0.12)',
            color: card.ok ? '#4ADE80' : '#F87171',
            border: card.ok ? '1px solid rgba(34,197,94,0.25)' : '1px solid rgba(220,38,38,0.25)',
            borderRadius: 999,
            padding: '4px 14px',
            fontSize: 13,
            fontWeight: 600,
            marginBottom: 20,
          }}
        >
          {card.badge}
        </span>
        <h1 style={{ fontSize: 22, marginBottom: 12, color: '#FAFAFA' }}>{card.heading}</h1>
        <p style={{ color: '#A1A1AA', lineHeight: 1.6, margin: 0 }}>
          {card.typeLabel ? (
            <>
              Your <strong>{card.typeLabel}</strong> {card.body}
            </>
          ) : (
            card.body
          )}
        </p>
        {card.recover && (
          <p style={{ marginTop: 16, fontSize: 14, lineHeight: 1.6 }}>
            <a href="/dsar" style={{ color: '#E11D48', textDecoration: 'none', fontWeight: 600 }}>
              Resubmit your request
            </a>
            {LEGAL.supportEmail && (
              <>
                {', or email '}
                <a
                  href={`mailto:${LEGAL.supportEmail}?subject=${encodeURIComponent('Privacy request verification problem')}`}
                  style={{ color: '#E11D48', textDecoration: 'none', fontWeight: 600 }}
                >
                  {LEGAL.supportEmail}
                </a>
                {' for help.'}
              </>
            )}
          </p>
        )}
        <p style={{ marginTop: 20, fontSize: 14 }}>
          <a href="/" style={{ color: '#E11D48', textDecoration: 'none', fontWeight: 600 }}>
            Return to Rize OS
          </a>
        </p>
      </div>
    </div>
  )
}
